import { useRef } from 'react'

import { Flex, Link, Loader, Text } from '@buffer-mono/popcorn'

import { CheckCapabilitiesCTA } from './CheckCapabilitiesCTA'
import { PAGE_SECTION_DESCRIPTION } from './config'
import type { LinkedInProfile } from './config'
import { Header } from './Header'
import { InfoBar } from './InfoBar'
import { SkillsTable } from './SkillsTable'
import { useLinkedInCapabilitiesSettings } from './useLinkedInCapabilitiesSettings'
import { useScrollToLinkedInCapabilitiesSection } from './useScrollToLinkedInCapabilitiesSection'
import commonStyles from '../../GeneralSettings.module.css'

type LinkedInVerifiedSkillsProps = {
  profile: LinkedInProfile
}

const getSectionDescription = (
  linkedInProfileUrl?: string | null,
  showPermissionsHint = false,
): React.JSX.Element => (
  <>
    Showcase capabilities on your{' '}
    {linkedInProfileUrl ? (
      <Link color="inherit" external href={linkedInProfileUrl}>
        LinkedIn profile
      </Link>
    ) : (
      'LinkedIn profile'
    )}{' '}
    based on your Buffer activity.
    {showPermissionsHint
      ? ' Enabling this will take you to LinkedIn to approve additional permissions for your connected account.'
      : ''}
  </>
)

const LinkedInVerifiedSkills = ({
  profile,
}: LinkedInVerifiedSkillsProps): React.JSX.Element | null => {
  const sectionRef = useRef<HTMLDivElement>(null)
  useScrollToLinkedInCapabilitiesSection(sectionRef)
  const {
    capabilities,
    checkCapabilitiesErrorMessage,
    chooseCapabilityPopoverCapabilityKey,
    displayedSelectedCapabilityKey,
    error,
    hasLoadedCapabilities,
    isChannelLocked,
    isCheckingCapabilities,
    isChooseCapabilityPopoverOpen,
    isInitialLoading,
    isLinkedInPage,
    isRefreshPopoverOpen,
    isSwitchDisabled,
    isSwitchOn,
    lastCheckedAt,
    linkedInProfileUrl,
    sectionTitle,
    shouldFetchCapabilities,
    shouldShowChooseCapabilityPopover,
    shouldShowRefreshPopover,
    handleCapabilitySelect,
    handleCheckCapabilities,
    handleChooseCapabilityPopoverOpenChange,
    handleRefreshChannel,
    handleSwitchChange,
    handleTableToggle,
    setIsRefreshPopoverOpen,
  } = useLinkedInCapabilitiesSettings(profile)

  if (profile.service !== 'linkedin') {
    return null
  }

  return (
    <div
      className={commonStyles.fullWidth}
      data-testid="linkedin-verified-skills-wrapper"
      ref={sectionRef}
    >
      <Flex direction="column" gap="md">
        <Header
          title={sectionTitle}
          description={
            isLinkedInPage
              ? PAGE_SECTION_DESCRIPTION
              : getSectionDescription(
                  linkedInProfileUrl,
                  shouldShowRefreshPopover,
                )
          }
          isDisabledState={isLinkedInPage}
          isSwitchOn={isSwitchOn}
          isSwitchDisabled={isSwitchDisabled}
          isRefreshPopoverOpen={isRefreshPopoverOpen}
          shouldShowRefreshPopover={shouldShowRefreshPopover}
          onRefreshChannel={handleRefreshChannel}
          onRefreshPopoverOpenChange={setIsRefreshPopoverOpen}
          onSwitchChange={handleSwitchChange}
        />
        {isLinkedInPage ? <InfoBar /> : null}
        {shouldFetchCapabilities && isInitialLoading ? (
          <Flex justify="center" align="center" fullWidth>
            <Loader />
          </Flex>
        ) : null}
        {shouldFetchCapabilities && error ? (
          <Text color="subtle">Unable to load LinkedIn capabilities.</Text>
        ) : null}
        {shouldFetchCapabilities && hasLoadedCapabilities && !error ? (
          <>
            <CheckCapabilitiesCTA
              isChannelLocked={isChannelLocked}
              isChecking={isCheckingCapabilities}
              lastCheckedAt={lastCheckedAt}
              errorMessage={checkCapabilitiesErrorMessage}
              onCheck={handleCheckCapabilities}
            />
            <SkillsTable
              capabilities={capabilities}
              isSelectionDisabled={isChannelLocked}
              selectedCapabilityKey={displayedSelectedCapabilityKey}
              chooseCapabilityPopoverCapabilityKey={
                chooseCapabilityPopoverCapabilityKey
              }
              isChooseCapabilityPopoverOpen={isChooseCapabilityPopoverOpen}
              shouldShowChooseCapabilityPopover={
                shouldShowChooseCapabilityPopover
              }
              onChooseCapabilityPopoverOpenChange={
                handleChooseCapabilityPopoverOpenChange
              }
              onCapabilitySelect={handleCapabilitySelect}
              onTableToggle={handleTableToggle}
            />
          </>
        ) : null}
      </Flex>
    </div>
  )
}

export {
  CheckLinkedInCapabilities,
  GetLinkedInVerifiedSkills,
  SetLinkedInCapabilityToDisplayOnNetwork,
  ToggleLinkedInVerifiedSkillSynchronization,
} from './useLinkedInCapabilitiesSettings'
export { LinkedInVerifiedSkills }
